// Copyright (c) Bentley Systems
import { Button, Checkbox, Dialog } from "@itwin/itwinui-react";
import * as React from "react";
import { getPropertyOrDefaultValue } from "../Helpers/helper-functions";
import { useEffect } from "react";
import { EfColorPicker } from "../Eform/EfColorPicker";
import { useColorPicker } from "./useColorPicker";

export type ConfirmationDialogProperties = {
  caption: string;
  text: string;
  okText?: string;
  cancelText?: string;
  isOpen: boolean;
  onConfirmationAction: () => void;
  onCancelAction: () => void;
  showCheckbox?: boolean;
  checkboxLabel?: string;
  checkboxChecked?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  showColorPicker?: boolean;
  colorPickerLabel?: string;
  colorPickerValue?: string;
  onColorChange?: (color: string | null) => void;
};

export const ConfirmationDialog = (props: ConfirmationDialogProperties) => {
  const [isOpen, setIsOpen] = React.useState(props.isOpen);
  const [checkboxValue, setCheckboxValue] = React.useState(
    props.checkboxChecked ?? false,
  );

  const { colorValue, handleColorChange } = useColorPicker({
    initialColor: props.colorPickerValue,
    required: true,
    hideNoneButton: true,
    onChange: props.onColorChange,
  });

  const closeDialog = () => {
    setIsOpen(false);
  };

  function Close() {
    closeDialog();
    props.onCancelAction();
  }

  const onClose = () => {
    Close();
  };

  const okButtonHandle = () => {
    closeDialog();
    props.onConfirmationAction();
  };

  const cancelButtonHandle = () => {
    Close();
  };

  useEffect(() => {
    setIsOpen(props.isOpen);
    if (props.isOpen) {
      // Reset checkbox value when dialog opens
      setCheckboxValue(props.checkboxChecked ?? false);
    }
  }, [props.isOpen, props.checkboxChecked]);

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        closeOnEsc
        closeOnExternalClick
        preventDocumentScroll
        trapFocus
        setFocus
        isDismissible
      >
        <Dialog.Main>
          <Dialog.TitleBar titleText={props.caption} />
          <Dialog.Content>
            {props.text}
            {props.showCheckbox && (
              <div style={{ marginTop: "16px" }}>
                <Checkbox
                  label={props.checkboxLabel || "Checkbox"}
                  checked={checkboxValue}
                  onChange={(event) => {
                    const newValue = event.target.checked;
                    setCheckboxValue(newValue);
                    if (props.onCheckboxChange) {
                      props.onCheckboxChange(newValue);
                    }
                  }}
                />
              </div>
            )}
            {props.showColorPicker && (
              <div style={{ marginTop: "16px" }}>
                <EfColorPicker
                  required={true}
                  label={props.colorPickerLabel || "Default color"}
                  selectedValue={colorValue}
                  hideNoneButton={true}
                  onChange={handleColorChange}
                />
              </div>
            )}
          </Dialog.Content>
          <Dialog.ButtonBar>
            <Button styleType="high-visibility" onClick={okButtonHandle}>
              {getPropertyOrDefaultValue(props.okText, "Ok")}
            </Button>
            <Button onClick={cancelButtonHandle}>
              {getPropertyOrDefaultValue(props.cancelText, "Cancel")}
            </Button>
          </Dialog.ButtonBar>
        </Dialog.Main>
      </Dialog>
    </>
  );
};
